import SearchResults from '../../../Models/SearchResults';
import * as Immutable from 'immutable';
import { ID, type ReduxAction, type SearchState, type SearchType } from '@app/types';
import { actionTypes } from '@actions/search';
import Occasion from '@support/Occasion';
import {
  clearSearchTerm,
  contactManagementUpdaters,
  fetchSearchResultsFailed,
  fetchSearchResultsRequested,
  fetchSearchResultsSucceeded,
  setSearchSorting,
  setUserInSearchCollection,
} from './createSearchReducer';

const cities = ['NYC', 'SF', 'London', 'Paris', 'Shanghai', 'Miami', 'Boston'];
const countries = ['France', 'Spain', 'Canada', 'China', 'Japan', 'India', 'Egypt', 'Malaysia'];
const regions = ['California', 'Texas', 'Northeast', 'South America', 'Africa', 'Asia', 'Europe'];

const initialSearchState = {
  type: 'search' as SearchType,
  term: '',
  results: new SearchResults(),
  recent: [],
  sorting: {},
  deletedIDCacheToExpire: Immutable.Set<ID>(),
  tips: {
    interest: ['Art', 'Travel', 'Books', 'TV', 'Movies', 'Museums', 'Photography'],
    org: ['CAA', 'Apple', 'Mount Sinai', 'NBC', 'The New Yorker', 'Nike', 'Coca Cola'],
    location: cities.concat(countries).concat(regions),
    social_media: [
      'github',
      'tumblr',
      'instagram',
      'medium',
      'reddit',
      'youtube',
      'tumblr',
      'vimeo',
      'soundcloud',
      'facebook',
    ],
    date: ['recently', 'a while ago'],
    relationship: ['met', 'texted', 'emailed'],
    education: ['masters', 'phd'],
    content_type: ['notes', 'reminders'],
    upcoming_birthdays: ['upcoming birthdays'],
    cleanups: ['lastMet:<2018'],
    all: ['all'],
  },
};

function buildRecent(recent, term) {
  recent = recent.filter((item) => item.term !== term && item.term);

  if (term) {
    recent.unshift({ term, date: new Occasion() });
  }

  return recent.slice(0, 6);
}

export function searchReducer(
  state: SearchState = initialSearchState,
  { type, payload }: ReduxAction,
) {
  const contactsUpdater = contactManagementUpdaters[type];

  if (contactsUpdater) {
    return contactsUpdater(state, payload);
  }

  switch (type) {
    case actionTypes.SET_SEARCH_TERM:
      const extra: any = {};

      if (payload.updateRecent) {
        extra.recent = buildRecent(state.recent, payload.term);
      }

      return { ...state, term: payload.term, ...extra };

    case actionTypes.SAVE_SEARCH_TERM:
      return { ...state, recent: buildRecent(state.recent || [], payload.term) };

    case actionTypes.CLEAR_SEARCH_HISTORY:
      return { ...state, recent: [] };

    case actionTypes.REMOVE_SEARCH_TERM:
      return {
        ...state,
        recent: (state.recent || []).filter((item) => item.term !== payload.term),
      };

    case 'LOAD_LOCAL_STORE_SUCCEEDED':
      const sorting = payload.get('search_sorting') || { name: 'closeness', direction: 'desc' };
      const searchRecent = payload.get('search_recent') || [];
      return { ...state, sorting, recent: searchRecent };

    case 'FETCH_SEARCH_RESULTS_REQUESTED':
      return fetchSearchResultsRequested(state);

    case 'FETCH_SEARCH_RESULTS_SUCCEEDED':
      return fetchSearchResultsSucceeded(state, payload);

    case 'FETCH_SEARCH_RESULTS_FAILED':
      return fetchSearchResultsFailed(state, payload);

    case actionTypes.CLEAR_SEARCH_TERM:
      return clearSearchTerm(state, payload);

    case 'SET_SEARCH_SORTING':
      return setSearchSorting(state, payload);

    case 'SET_USER':
      return setUserInSearchCollection(state, payload);

    case 'FETCH_SEARCH_TIPS_SUCCEEDED':
      return {
        ...state,
        tips: {
          ...state.tips,
          ...payload.response,
        },
      };

    default:
      return state;
  }
}
